import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useSeo } from "../../hooks/useSeo";
import { articles, channels, channelName } from "../../mocks/chigua";
import ArticleCard from "../../components/feature/ArticleCard";

const PER_PAGE = 6;

export default function Baoliao() {
  const [searchParams, setSearchParams] = useSearchParams();
  const channel = searchParams.get("channel") || "all";
  const q = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(q);
  const [visible, setVisible] = useState(PER_PAGE);

  const filtered = useMemo(() => {
    let list = articles;
    if (channel !== "all") list = list.filter((a) => a.channel === channel);
    const kw = q.trim();
    if (kw) {
      list = list.filter(
        (a) =>
          a.title.includes(kw) ||
          a.summary.includes(kw) ||
          a.tags.some((t) => t.includes(kw)) ||
          channelName(a.channel).includes(kw)
      );
    }
    return list;
  }, [channel, q]);

  const applyChannel = (key: string) => {
    setVisible(PER_PAGE);
    const next: Record<string, string> = {};
    if (key !== "all") next.channel = key;
    if (q) next.q = q;
    setSearchParams(next);
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setVisible(PER_PAGE);
    const next: Record<string, string> = {};
    if (channel !== "all") next.channel = channel;
    if (keyword.trim()) next.q = keyword.trim();
    setSearchParams(next);
  };

  useSeo({
    title: channel !== "all" ? `${channelName(channel)}吃瓜爆料 - 今天吃瓜` : "吃瓜爆料合集｜今日吃瓜爆料汇总 - 今天吃瓜",
    description:
      channel !== "all"
        ? `「今天吃瓜」${channelName(channel)}频道吃瓜爆料合集，聚合${channelName(channel)}最新爆料、吃瓜帖与讨论热度，第一时间掌握新瓜动态。`
        : "今天吃瓜吃瓜爆料库，聚合娱乐八卦、社会热点、网络热议、行业内幕等最新爆料与吃瓜合集，按热度与时间持续更新今日瓜料汇总。",
    keywords: "吃瓜爆料,今日爆料,吃瓜合集,最新吃瓜,爆料汇总",
    path: "/baoliao",
    schemas: [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://emotechai.cn/" },
          { "@type": "ListItem", position: 2, name: "吃瓜爆料", item: "https://emotechai.cn/baoliao" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "吃瓜爆料合集 - 今天吃瓜",
        inLanguage: "zh-CN",
        url: "https://emotechai.cn/baoliao",
        mainEntity: articles.map((a) => ({ "@type": "Article", headline: a.title, url: `https://emotechai.cn/baoliao/${a.id}` })),
      },
    ],
  });

  const tabs = [{ key: "all", name: "全部吃瓜", icon: "ri-apps-2-line" }, ...channels];
  const shown = filtered.slice(0, visible);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      {/* breadcrumb */}
      <nav className="flex items-center gap-1.5 text-xs text-foreground-500" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-600">首页</Link>
        <i className="ri-arrow-right-s-line" aria-hidden="true" />
        <span className="text-foreground-800">吃瓜爆料</span>
      </nav>

      <div className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            {channel !== "all" ? `${channelName(channel)}吃瓜爆料` : "吃瓜爆料库"}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600">
            {channel !== "all"
              ? `「${channelName(channel)}」频道最新吃瓜合集与爆料汇总，按热度实时更新，帮你不错过任何一处发酵中的瓜。`
              : "聚合娱乐八卦、社会热点、网络热议与行业内幕的最新爆料与吃瓜合集，按热度实时排序，一页看遍今日爆料汇总。"}
          </p>
        </div>
        <form onSubmit={submitSearch} className="w-full max-w-md lg:w-80">
          <label className="flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2.5 transition focus-within:border-primary-400">
            <i className="ri-search-line text-foreground-500" aria-hidden="true" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜瓜：标题 / 标签 / 关键词"
              className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button type="submit" className="whitespace-nowrap rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-background-50 hover:bg-primary-600">
              搜索
            </button>
          </label>
        </form>
      </div>

      {/* channel tabs */}
      <div className="mt-7 flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => applyChannel(t.key)}
            className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium whitespace-nowrap transition ${
              channel === t.key
                ? "bg-primary-500 text-background-50"
                : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
            }`}
          >
            <i className={t.icon} aria-hidden="true" />
            {t.name}
          </button>
        ))}
      </div>

      {/* result meta */}
      <div className="mt-6 flex flex-wrap items-center justify-between gap-2 border-b border-background-200 pb-3 text-xs text-foreground-500">
        <span>
          共找到 <strong className="text-primary-700">{filtered.length}</strong> 条吃瓜爆料
          {q && <> · 关键词「<span className="text-primary-700">{q}</span>」</>}
        </span>
        {q && (
          <button
            type="button"
            onClick={() => {
              setKeyword("");
              const next: Record<string, string> = {};
              if (channel !== "all") next.channel = channel;
              setSearchParams(next);
            }}
            className="flex items-center gap-1 font-medium text-primary-600 hover:text-primary-700"
          >
            <i className="ri-close-circle-line" aria-hidden="true" />
            清除筛选
          </button>
        )}
      </div>

      {/* list */}
      {shown.length > 0 ? (
        <div className="mt-5 flex flex-col gap-4">
          {shown.map((a) => (
            <ArticleCard key={a.id} article={a} layout="row" />
          ))}
        </div>
      ) : (
        <div className="mt-10 flex flex-col items-center gap-3 rounded-2xl border border-dashed border-background-300 py-16 text-center">
          <i className="ri-inbox-2-line text-4xl text-background-400" aria-hidden="true" />
          <p className="font-medium text-foreground-700">暂时没有找到相关爆料</p>
          <p className="text-sm text-foreground-500">换个关键词或频道试试，新瓜正在路上～</p>
        </div>
      )}

      {/* load more */}
      {visible < filtered.length && (
        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => setVisible((v) => v + PER_PAGE)}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition hover:border-primary-300 hover:text-primary-700"
          >
            <i className="ri-arrow-down-line" aria-hidden="true" />
            加载更多爆料（已显示 {shown.length}/{filtered.length}）
          </button>
        </div>
      )}
    </div>
  );
}